/*
 * Karved: keep the "Back of the cup" fee line in step with its cup.
 *
 * The builder adds the back fee (SKU KC-FEE-BACK, $5 a cup) as its own cart line, quantity = cup
 * quantity, linked to the cup by the `For design` property (last 6 characters of the cup's
 * `_design_id`, upper case). When a shopper changes the cup quantity in the cart, or removes the
 * cup, this sets the fee line to match: quantity = total cups with a back for that design, or 0
 * (removed) when no such cup is left. Changing the fee line's quantity on its own is put back the
 * same way. A fee line the shopper deletes is not re-added; the order webhook still checks the
 * amounts (orderGuard back-side-fee-missing/-refund) and flags that order for staff.
 *
 * Name fee lines (KC-FEE-NAME, one per named cup line) whose cup is removed are removed too: the cart
 * shows them under their cup with no remove button of their own (snippets/cart-products.liquid).
 * Art prep fee lines (one per design, can pay for several cups) are not touched.
 */
(function () {
  var FEE_SKU = 'KC-FEE-BACK';
  var SOURCE = 'karved-fee-sync';

  function prop(item, name) {
    var p = item && item.properties;
    var v = p && p[name];
    return typeof v === 'string' ? v.trim() : '';
  }

  /**
   * Pure: the cart line updates needed, as { [line key]: quantity }. Empty when in step.
   * @param {Array<{key: string, sku?: string|null, quantity: number, properties?: Record<string, unknown>|null}>} items
   */
  function backFeeUpdates(items) {
    var cupsByCode = {};
    var fees = [];
    (items || []).forEach(function (item) {
      if (item.sku === FEE_SKU) {
        fees.push(item);
        return;
      }
      var designId = prop(item, '_design_id');
      if (!designId || !prop(item, 'Back side')) return;
      var code = designId.slice(-6).toUpperCase();
      cupsByCode[code] = (cupsByCode[code] || 0) + item.quantity;
    });
    var updates = {};
    var seen = {};
    fees.forEach(function (fee) {
      var code = prop(fee, 'For design').toUpperCase();
      // No design code: not one of the builder's lines, so leave it (the order check counts it).
      if (!code) return;
      // A second fee line for the same cup is a duplicate: the first one carries the full count.
      var want = seen[code] ? 0 : cupsByCode[code] || 0;
      seen[code] = true;
      if (fee.quantity !== want) updates[fee.key] = want;
    });
    return updates;
  }

  var NAME_FEE_SKU = 'KC-FEE-NAME';

  /**
   * Pure: name fee lines (one per named cup line, linked by `For design`) whose cup is gone, set to 0.
   * The cart shows a name fee under its cup with no remove of its own, so removing the cup removes it.
   * @param {Array<{key: string, sku?: string|null, quantity: number, properties?: Record<string, unknown>|null}>} items
   */
  function nameFeeUpdates(items) {
    var cups = {};
    (items || []).forEach(function (item) {
      var designId = prop(item, '_design_id');
      if (designId) cups[designId.slice(-6).toUpperCase()] = true;
    });
    var updates = {};
    (items || []).forEach(function (item) {
      if (item.sku !== NAME_FEE_SKU) return;
      var code = prop(item, 'For design').toUpperCase();
      if (code && !cups[code] && item.quantity !== 0) updates[item.key] = 0;
    });
    return updates;
  }

  /** Pure: every fee line update (back fee in step with its cups, name fees whose cup is gone). */
  function feeUpdates(items) {
    var out = backFeeUpdates(items);
    var names = nameFeeUpdates(items);
    Object.keys(names).forEach(function (k) { out[k] = names[k]; });
    return out;
  }

  /** Pure: cart line count a shopper reads ("2 items"): cup and product lines only, no KC-FEE-* lines. */
  function shopperItemCount(items) {
    return (items || []).reduce(function (n, item) {
      return String(item.sku || '').indexOf('KC-FEE-') === 0 ? n : n + (Number(item.quantity) || 0);
    }, 0);
  }

  var api = {
    backFeeUpdates: backFeeUpdates, nameFeeUpdates: nameFeeUpdates, feeUpdates: feeUpdates, shopperItemCount: shopperItemCount,
  };
  if (typeof globalThis !== 'undefined') globalThis.KarvedFeeSync = api;
  if (typeof document === 'undefined' || typeof fetch === 'undefined') return;

  var routes = (window.Theme && window.Theme.routes) || {};
  var cartUrl = routes.cart_url || '/cart';
  var updateUrl = routes.cart_update_url || '/cart/update';
  var running = false;
  var again = false;

  function sectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function sync() {
    if (running) {
      again = true;
      return;
    }
    running = true;
    fetch(cartUrl + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return null;
        var updates = feeUpdates(cart.items);
        if (!Object.keys(updates).length) return null;
        var ids = sectionIds();
        var body = { updates: updates };
        if (ids.length) {
          body.sections = ids.join(',');
          body.sections_url = window.location.pathname;
        }
        return fetch(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (updated) {
            if (!updated || updated.errors) return;
            // Same shape as the theme's CartUpdateEvent, so the drawer, cart page and cart icon redraw.
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: updated,
                sourceId: SOURCE,
                data: { source: SOURCE, itemCount: updated.item_count, sections: updated.sections },
              },
            }));
          });
      })
      .catch(function (err) { console.warn('[Karved] back fee sync failed', err); })
      .then(function () {
        running = false;
        if (again) {
          again = false;
          sync();
        }
      });
  }

  api.sync = sync;

  var timer = null;
  document.addEventListener('cart:update', function (event) {
    var d = event && event.detail;
    if (d && (d.sourceId === SOURCE || (d.data && d.data.source === SOURCE))) return;
    clearTimeout(timer);
    timer = setTimeout(sync, 400);
  });

  // A page can open with the lines already out of step (a cup removed in another tab or by a direct
  // /cart/change.js call leaves its name fee behind). theme.liquid sets data-back-fee="1" whenever the
  // cart holds a KC-FEE-* line, so the cart page and the drawer (on every page) check once on load;
  // carts without a fee line make no extra request.
  var me = document.currentScript;
  if (me && me.getAttribute('data-back-fee') === '1') sync();
})();
